import AccountHeader from '@component/AccoutInfo';
import Advertise from '@component/Advertise';
import { InviteParams } from '@component/Invite/InviteContext';
import buyerCoupon from '@model/buyer-coupon';
import ActivitiesReportSendDataReport from '@quickService/ActivitiesReportSendDataReport';
import { loadMidasScript } from '@tencent/calendar-web-component-mobile-biz/es/Pay/getWeixinOpenId';
import { Button } from '@tencent/tea-component';
import AbnormalState from '@tencent/tpm-meeting-active/lib/AbnormalState';
import '@tencent/tpm-meeting-active/lib/AbnormalState/style';
import Layout from '@tencent/tpm-meeting-active/lib/Layout';
import { AppContext } from '@utils/browser/app-context';
import dataReport from '@utils/data-report';
import disableIpadScale from '@utils/disable-scale';
import isIpad from '@utils/is-ipad';
import { isMeetingApp } from '@utils/ua/is-mobile';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { memo, useContext, useEffect, useState, useMemo } from 'react';
import getServerProps, { ServerProps } from './buyer/controller/getServerProps';
import useAnchorTabs from './buyer/hooks/useAnchorTabs';
import {
  useExposure,
  useHostData,
  useInitData,
  useMyScheduleList,
  useScheduleList,
} from './buyer/hooks/useInitData';
import ErrorBoundary from '@component/ErrorBoundary';
import './index.scss';
import { isWechat, isWechatMiniProgram, loadScript } from '@tencent/web-base';
import useSignUp from './utils/signUp';
import getTextFromHtml from './utils/getTextFromHtml';

const { Body, Content } = Layout;

const CanvasRender = dynamic(() => import('./buyer/CanvasRender'));
const Pc = dynamic(() => import('./buyer/pc'));
const Mobile = dynamic(() => import('./buyer/mobile'));

export const getServerSideProps: GetServerSideProps = getServerProps;

const routerPage = (path: string) => {
  const url = `${window.location.origin}${process.env.basePath}${path}`;
  window.location.replace(url);
};

const Registration = (props: ServerProps) => {
  const [isApp, setIsApp] = useState(false);

  useEffect(() => {
    if (isIpad(navigator.userAgent)) {
      disableIpadScale();
    }
    loadMidasScript();
  }, []);

  useEffect(() => {
    if (!props.registrationData || !props.scheduleData) {
      const errMsg = `[registration-error]: ${JSON.stringify(props.pageData)}`;
      if (window?.nextAegis?.infoAll) {
        window?.nextAegis?.infoAll?.(errMsg);
      } else {
        window.AegisErrs = [errMsg];
      }
    }
  }, []);

  // 活动失效逻辑
  if (!props.registrationData) {
    return (
      <AbnormalState
        className="registration-error-page"
        state={'invalid'}
        title={'糟糕，内容已失效～'}
        desc={<p>页面不存在，点击下方按钮返回活动空间</p>}
        operation={
          <Button
            type="primary"
            onClick={() => {
              routerPage('/home');
            }}
          >
            返回活动空间
          </Button>
        }
      />
    );
  }

  const { eventid, couponList } = props;
  const { registrationData, scheduleData } = props;
  const myScheduleList = useMyScheduleList(eventid);
  const hostId = registrationData.activity_basic_info.host_id;
  const hostData = useHostData(hostId);
  const [price, selectId] = useInitData(registrationData);
  const [selectScheduleId, setSelectScheduleId] = useState(selectId);
  const { showScheduleList, actTime, dates, setCurr, curr } = useScheduleList(
    registrationData.activity_basic_info,
    scheduleData.scheduler_data_list,
    selectScheduleId,
  );
  const { anchorTabs, changeNavTab } = useAnchorTabs(registrationData, curr);
  const { userData } = useContext(AppContext);
  const router = useRouter();
  const { coupons } = buyerCoupon.setCouponList(couponList);
  const signUp = useSignUp(undefined, props.wxopenId);

  useEffect(() => {
    InviteParams.verifySignUpUrl(userData.userid);

    if (userData.userid) {
      ActivitiesReportSendDataReport({
        report_id: 'registration_page_expose',
      });
    }
  }, [userData]);

  useExposure('e#event_registrationpage#all#exposure', eventid, props.isCanvas);

  useEffect(() => {
    const channelId = router?.query?.ch;
    if (channelId !== undefined) {
      dataReport('e#registration#all#exposure', {
        channel_id: router?.query?.ch || '0',
        href: location.href,
        event_id: eventid,
      });
    }
    if (!props.isCanvas && isWechatMiniProgram()) {
      const redirectToMiniProgram = async () => {
        loadScript('https://res.wx.qq.com/open/js/jweixin-1.6.0.js');

        setTimeout(() => {
          window.wx?.miniProgram.redirectTo({
            url: `/registration/pages/registration/index?type=jump&targeturl=${encodeURIComponent(
              `/event/${eventid}/registration`,
            )}`,
          });
        }, 400);
      };
      // 小程序内访问的话，需要直接跳到原生小程序的页面
      redirectToMiniProgram();
    }
  }, []);

  useEffect(() => {
    setIsApp(isMeetingApp());
  }, [registrationData.activity_basic_info.title]);

  // 报名页title
  const title = useMemo(() => {
    if (typeof window === 'undefined') return '活动详情';
    if (isMeetingApp()) return '活动详情';
    return registrationData.activity_basic_info.title;
  }, [registrationData.activity_basic_info.title]);

  useEffect(() => {
    if (userData.userid) {
      const redirectParams = sessionStorage.getItem('redirectParams');
      if (isWechat() && redirectParams) {
        const params = JSON.parse(redirectParams);
        const scheduleId = params?.id || '';
        const type = params?.t || '';
        signUp(eventid, scheduleId, type);
      }
      sessionStorage.removeItem('redirectParams');
    }
  }, [userData]);

  const seoTitle = () => {
    if (isApp || props.isMobile) {
      return title;
    }
    return `${registrationData.activity_basic_info.title}_腾讯会议`;
  };
  const description = () => {
    const { content } = registrationData.activity_introduction;
    const extractedText = getTextFromHtml(content);
    if (!extractedText) {
      return '';
    }
    const text =
      extractedText.substring(0, 80).lastIndexOf('。') === -1
        ? `${extractedText.substring(0, 80)}。`
        : extractedText.substring(0, 80);

    return text;
  };

  const keyWords = () => {
    const labels = props.activityLabels || [];
    const joinedText = labels.map((item) => item.text).join(',');
    return `腾讯会议活动,腾讯会议,活动广场,举办活动,活动${
      joinedText ? `,${joinedText}` : joinedText
    }`;
  };

  const seoTdk = (
    <>
      <title>{seoTitle()}</title>
      <meta name="description" content={description()} />
      <meta name="keyWords" content={keyWords()} />
    </>
  );

  // 使用canvas制作报名页
  if (props.isCanvas) {
    return (
      <>
        <Head>{seoTdk}</Head>
        <CanvasRender
          {...props.canvasData}
          eventId={eventid}
          hostData={hostData}
          price={price}
          registrationData={registrationData}
          scheduler_data_list={scheduleData.scheduler_data_list}
          myScheduleList={myScheduleList}
          wxopenid={props.wxopenId}
        />
      </>
    );
  }

  return (
    <>
      <Head>{seoTdk}</Head>
      <ErrorBoundary>
        <Layout className="pg-active-details">
          <Body>
            {isApp ? null : (
              <div className="registration-header">
                <AccountHeader />
              </div>
            )}
            <Content showBackButton={false} backButtonText={null}>
              <Mobile
                {...props}
                registrationData={registrationData}
                myScheduleList={myScheduleList}
                hostData={hostData}
                selectScheduleId={selectScheduleId}
                setSelectScheduleId={setSelectScheduleId}
                showScheduleList={showScheduleList}
                anchorTabs={anchorTabs}
                price={price}
                actTime={actTime}
                changeNavTab={changeNavTab}
                dates={dates}
                setCurr={setCurr}
                curr={curr}
                couponList={coupons}
              />
              <Pc
                {...props}
                registrationData={registrationData}
                myScheduleList={myScheduleList}
                hostData={hostData}
                selectScheduleId={selectScheduleId}
                setSelectScheduleId={setSelectScheduleId}
                showScheduleList={showScheduleList}
                anchorTabs={anchorTabs}
                price={price}
                actTime={actTime}
                dates={dates}
                setCurr={setCurr}
                curr={curr}
                couponList={coupons}
              />
              <Advertise />
            </Content>
          </Body>
        </Layout>
      </ErrorBoundary>
    </>
  );
};

export default memo(Registration);
